import { useState } from 'react';
import { Button, cn, Typography } from '@prenuvo/halo-foundation';

import { FCT } from '@/i18n';
import { Editor } from '@/modules/reporting/shared/component';
import { useStudyGuard } from '@/shared/guard';
import { formatEditorString } from '@/shared/util';

import { useCreateActivityLog } from './use-create-activity-log.hook';
import { useCreateStudyLog } from './use-create-study-log.hook';

export type PatientContactOutcomeSectionProps = {
  closeDialog: () => void;
  reason: string;
};

export function PatientContactOutcomeSection({
  closeDialog,
  reason,
}: PatientContactOutcomeSectionProps) {
  const { patient } = useStudyGuard();
  const [callDescription, setCallDescription] = useState<string>('');
  const createActivityLog = useCreateActivityLog();
  const createStudyLog = useCreateStudyLog();

  const handleSubmit = () => {
    const payload = {
      fullObject: {
        descriptionOfCall: callDescription,
      },
    };

    const studyLogPayload = {
      body: `FCT access to Patient Info - Reason: ${reason}, Description of call: ${callDescription}`,
    };

    createActivityLog(payload, () => {
      createStudyLog(studyLogPayload, closeDialog);
    });
  };

  return (
    <div>
      <Typography className="text-neutral-400 dark:text-neutral-400" variant="paragraph-sm">
        {FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.LEGAL_NAME}
      </Typography>
      <Typography as="p" className="mb-4 text-neutral-50">
        {patient.lastname}, {patient.firstname}
      </Typography>
      <Typography className="text-neutral-400 dark:text-neutral-400" variant="paragraph-sm">
        {FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.PHONE_NUMBER}
      </Typography>
      <Typography as="p" className="mb-4 text-neutral-50">
        {patient.phone_number}
      </Typography>
      <Typography className="mb-2 text-neutral-400 dark:text-neutral-400" variant="paragraph-sm">
        {FCT.REPORTING.HEADER.CONTACT_DETAILS.PATIENT_OUTCOME_INPUT_TITLE}
        <span className="ml-1 text-red-500 after:content-['*'] dark:text-red-500" />
      </Typography>
      <div
        className={cn(
          'm-auto min-h-20 rounded-lg border-neutral-800 bg-neutral-900 p-2 px-3 text-neutral-50 focus:outline-none dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50 custom-scrollbar',
        )}
      >
        <Editor
          className="w-full whitespace-pre-wrap break-words [&>.ql-editor]:min-h-20"
          onChange={(val) => setCallDescription(val)}
          value={String(callDescription ?? '')}
        />
      </div>
      <div className="mb-2 mt-4 flex justify-end">
        <Button
          className={{ root: 'px-8' }}
          data-testid="submit-button"
          disabled={!formatEditorString(callDescription)}
          onClick={handleSubmit}
          variant="primary"
        >
          {FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.SUBMIT}
        </Button>
      </div>
    </div>
  );
}
